// ----------------------------------------------------------------------------
// ChordWatchProvider — global, page-independent tracking of in-progress chord
// analyses. Mounted once at the app root so processing keeps being watched even
// after the user navigates away from the Chord AI Library. When an analysis
// finishes it persists the result to the church record and fires a browser
// notification (the "bell"). Pending items are persisted in localStorage so a
// page refresh resumes watching.
// ----------------------------------------------------------------------------

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { chordUploadsApi, chordsApi, type ChordUploadStatus } from '@app/api/chords';
import { useAuth } from '@app/auth/AuthContext';
import { logger } from '@app/utils/logger';

interface WatchItem {
  sourceId: string;
  churchId: string;
  title: string;
  startedAt: number;
}

interface CompletedEvent {
  sourceId: string;
  status: ChordUploadStatus;
  title: string;
  at: number;
}

interface ChordWatchContextValue {
  /** Start watching an analysis; notifies on completion even after navigation. */
  watch: (sourceId: string, title: string) => void;
  /** Source ids currently being watched. */
  pendingIds: string[];
  /** Last finalized analysis; pages can use it to refresh their list. */
  lastCompleted: CompletedEvent | null;
}

const ChordWatchContext = createContext<ChordWatchContextValue | null>(null);

const STORAGE_KEY = 'churchnow.chordWatch.v1';
const POLL_MS = 5000;
// Stop watching an item that never finishes (worker down, etc.) so we don't
// poll forever. 45 minutes comfortably covers a Demucs stem-separation run.
const MAX_AGE_MS = 45 * 60 * 1000;

function loadFromStorage(): WatchItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveToStorage(items: WatchItem[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

/** Ask for notification permission (no-op if unsupported or already decided). */
export function ensureNotificationPermission() {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'default') {
    Notification.requestPermission().catch(() => {});
  }
}

export function notifyChord(heading: string, body: string) {
  if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
    try {
      const n = new Notification(heading, { body, icon: '/favicon.ico' });
      n.onclick = () => {
        window.focus();
        n.close();
      };
      return;
    } catch (err) {
      // Some browsers only allow notifications from a service worker; fall through.
      logger.warn?.('Chord notification fallback:', err);
    }
  }
  // Best-effort fallback so the event is still surfaced in the console/logs.
  logger.info(`${heading}: ${body}`);
}

function fireNotification(title: string, status: ChordUploadStatus) {
  const ok = status === 'completed';
  notifyChord(
    ok ? 'Chord analysis complete' : 'Chord analysis failed',
    ok
      ? `"${title}" is ready in Song Studio.`
      : `"${title}" could not be analyzed. Please try again.`,
  );
}

export const ChordWatchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<WatchItem[]>(() => loadFromStorage());
  const [lastCompleted, setLastCompleted] = useState<CompletedEvent | null>(null);
  const tickingRef = useRef(false);

  useEffect(() => {
    saveToStorage(items);
  }, [items]);

  const watch = useCallback(
    (sourceId: string, title: string) => {
      const churchId = user?.churchId;
      if (!churchId || !sourceId) return;
      ensureNotificationPermission();
      setItems((prev) =>
        prev.some((i) => i.sourceId === sourceId)
          ? prev
          : [...prev, { sourceId, churchId, title: title || 'Song', startedAt: Date.now() }],
      );
    },
    [user?.churchId],
  );

  useEffect(() => {
    if (items.length === 0) return;

    const timer = setInterval(async () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      try {
        for (const item of items) {
          // Give up on stale items so we never poll indefinitely.
          if (Date.now() - item.startedAt > MAX_AGE_MS) {
            setItems((prev) => prev.filter((i) => i.sourceId !== item.sourceId));
            continue;
          }
          try {
            const analysis = await chordsApi.getAnalysis(item.sourceId);
            if (analysis.status === 'completed' || analysis.status === 'failed') {
              try {
                await chordUploadsApi.update(item.churchId, item.sourceId, {
                  status: analysis.status,
                  chords: analysis.chords,
                  detectedKey: analysis.detectedKey,
                  bpm: analysis.bpm,
                });
              } catch (err) {
                logger.error('Chord watch: church record update failed:', err);
              }
              fireNotification(item.title, analysis.status);
              setItems((prev) => prev.filter((i) => i.sourceId !== item.sourceId));
              setLastCompleted({
                sourceId: item.sourceId,
                status: analysis.status,
                title: item.title,
                at: Date.now(),
              });
            }
          } catch (err) {
            const httpStatus = (err as { response?: { status?: number } })?.response?.status;
            // A 404 means the worker has no record of this analysis (orphaned or
            // lost, e.g. a stale pending record after the worker DB was reset).
            // After a short grace period, stop watching and mark it failed so the
            // UI clears instead of polling 404 forever.
            if (httpStatus === 404 && Date.now() - item.startedAt > 15000) {
              try {
                await chordUploadsApi.update(item.churchId, item.sourceId, { status: 'failed' });
              } catch (updateErr) {
                logger.error('Chord watch: church record update failed:', updateErr);
              }
              setItems((prev) => prev.filter((i) => i.sourceId !== item.sourceId));
              setLastCompleted({ sourceId: item.sourceId, status: 'failed', title: item.title, at: Date.now() });
            } else if (httpStatus !== 404) {
              // Genuine transient error (network / 5xx); keep retrying until MAX_AGE_MS.
              logger.error('Chord watch: poll failed:', err);
            }
          }
        }
      } finally {
        tickingRef.current = false;
      }
    }, POLL_MS);

    return () => clearInterval(timer);
  }, [items]);

  return (
    <ChordWatchContext.Provider value={{ watch, pendingIds: items.map((i) => i.sourceId), lastCompleted }}>
      {children}
    </ChordWatchContext.Provider>
  );
};

export function useChordWatch(): ChordWatchContextValue {
  const ctx = useContext(ChordWatchContext);
  if (!ctx) {
    // Safe no-op fallback so the hook works even if the provider is absent.
    return { watch: () => {}, pendingIds: [], lastCompleted: null };
  }
  return ctx;
}
